import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListIdentifiers } from 'src/features/identifiers/api/useListIdentifiers';
import { identifierValidityBadgeOptions } from 'src/features/identifiers/utils/constants';
import { identifiersMessages } from 'src/features/identifiers/utils/messages';
import { Identifier } from 'src/features/identifiers/utils/types';
import { identifierTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import CellDate from 'src/registry/components/table/cells/CellDate';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

type IdentifiersTableProps = {
    entityId?: string;
};

const columnHelper = createColumnHelper<Identifier>();

const IdentifiersTable = ({ entityId }: IdentifiersTableProps) => {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({});

    const { parkingId } = useParking();

    const {
        data: listIdentifiers,
        isError,
        isLoading,
    } = useListIdentifiers({
        parkingId: parkingId ?? '',
        ...(entityId && { entity: entityId }),
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const columns = [
        columnHelper.accessor('value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.value),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableHiding: true,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: identifierTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('is_used', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(identifiersMessages.isUsed),
            },
        }),
        columnHelper.accessor('validity', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(identifiersMessages.validity),
                badgeOptions: identifierValidityBadgeOptions,
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(membershipMessages.validFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDate,
            meta: {
                title: formatMessage(membershipMessages.validTo),
            },
        }),
    ];

    return (
        <DataTable
            columns={columns as any}
            data={listIdentifiers}
            pagination={pagination}
            sorting={sorting}
            search={search}
            semantics={{
                title: formatMessage(identifiersMessages.identifiers),
                entryName: formatMessage(identifiersMessages.identifier).toLowerCase(),
                entryNames: formatMessage(identifiersMessages.identifiers).toLowerCase(),
                placeholder: formatMessage(identifiersMessages.identifier).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default IdentifiersTable;
